import Select from "lib/common/controls/Select";
import DateRange, {
    dateToString,
    isDateRangeValid,
} from "lib/hex/views/components/DateRange";
import OptionGroup from "lib/hex/views/components/OptionGroup";
import { registerForm } from "lib/hex/views/formRegistry";
import PropTypes from "prop-types";
import React from "react";
import useKeyValueState from "lib/hex/views/components/useKeyValueState";
import {
    stringifyJson,
    unstringifyJson,
} from "lib/hex/views/components/serializeParams";

const FormView = ({onChange, state, data}) => (
    <div>
        <div className="form-section form-horizontal">
            <div className="form-section-inner">
                <h3>Participant ID</h3>
                <div className="form-group">
                    <div className="col-sm-12">
                        <Select
                            name="particid"
                            className="participant form-control"
                            onCommitChange={(v) => onChange("particid", v)}
                            value={state.particid}
                            options={data.particid}
                            required
                        />
                    </div>
                </div>
                <DateRange
                    start={state.start}
                    end={state.end}
                    min={data.daterange.from}
                    max={data.daterange.to}
                    onChange={onChange}
                    disabled={!state.particid}
                    required
                />
            </div>
        </div>
        <div className="form-section form-horizontal">
            <OptionGroup
                state={state.optionalFields}
                data={data.optionalFields}
                expanded={false}
                onChange={(value) => onChange("optionalFields", value)}
            />
        </div>
    </div>
);

const selectShape = PropTypes.arrayOf(
    PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
    })
);

FormView.propTypes = {
    onChange: PropTypes.func.isRequired,
    state: PropTypes.shape({
        start: PropTypes.string,
        end: PropTypes.string,
        particid: PropTypes.string,
        dateFormat: PropTypes.string,
        optionalFields: PropTypes.object,
    }),
    data: PropTypes.shape({
        particid: selectShape,
        daterange: PropTypes.shape({
            from: PropTypes.array,
            to: PropTypes.array
        }),
        optionalFields: PropTypes.array,
    }),
};

const initialStateWithDefaults = (state = {}) => {
    const newState = {
        start: dateToString(state.start),
        end: dateToString(state.end),
        particid: state.particid || "",
        optionalFields: state.optionalFields || {},
    };
    return unstringifyJson(newState, "optionalFields");
};

const serverDataParams = params =>
    stringifyJson(params, "optionalFields");

const initialData = {
    particid: [],
    daterange: { from: [], to: [] },
    optionalFields: [],
};

const ThoracicHarvestForm = ({
                                 initialState,
                                 needServerData,
                                 onReady,
                                 onNotReady,
                             }) => {
    const [ state, setState ] = React.useState(() =>
        initialStateWithDefaults(initialState));
    React.useEffect(
        () => setState(initialStateWithDefaults(initialState)),
        [ initialState ],
    );
    const [ data, setDataKey ] = useKeyValueState(initialData);

    React.useEffect(() => {
        needServerData({
            endpoint: "/participants",
            loadingMessage: "Loading Participants...",
            onData: (data) => {
                data = data.map(it => ({
                    label: `${it.particid}: ${it.hospname}`,
                    value: it.particid,
                }));
                data.unshift({ label: "", value: "" });
                setDataKey("particid", data);
            },
        });
    }, []);

    React.useEffect(() => {
        needServerData({
            endpoint: "/optional-fields",
            loadingMessage: "Loading Available Fields...",
            onData: (data) => {
                const fields = data.entities;
                if (_.isEmpty(state.optionalFields)) {
                    // initially all fields are selected if none were provided.
                    const selected = {};
                    fields.forEach(f => {
                        selected[f.key] = f.fields.map(it => it.key);
                    });
                    handleChange("optionalFields", selected);
                }
                setDataKey("optionalFields", fields);
            },
        });
    }, []);

    React.useEffect(() => {
        const { particid, datavrsns, surgeons } = state;
        needServerData({
            endpoint: "/date-range",
            loadingMessage: "Loading Available Date Range...",
            params: serverDataParams({
                particid,
                datavrsns,
                surgeons,
            }),
            onData: (data) =>
                setDataKey("daterange", { from: data.from, to: data.to }),
        });
    }, [ state.particid, state.datavrsns, state.surgeons ]);

    React.useEffect(() => {
        if (isDateRangeValid(state.start, state.end) && !!state.particid) {
            onReady(serverDataParams(state));
        } else {
            onNotReady();
        }
    }, [ state ]);

    const handleChange = React.useCallback(
        (key, value) =>
            setState(s => ({ ...s, [key]: value })),
        [ setState ],
    );

    return (
        <FormView
            state={state}
            data={data}
            onChange={handleChange}
        />
    );
};

ThoracicHarvestForm.propTypes = {
    initialState: PropTypes.shape({
    }),
    needServerData: PropTypes.func.isRequired,
    onReady: PropTypes.func.isRequired,
    onNotReady: PropTypes.func.isRequired,
};

ThoracicHarvestForm.canRerun = params => !!(params && params.particid);

ThoracicHarvestForm.apiPrefix = "thoracic";

registerForm("thoracic-harvest", ThoracicHarvestForm);



// WEBPACK FOOTER //
// ./export_forms/api-thoracic-2026.03.10-jar/hybrid-jsclient/ThoracicHarvestForm.js